/**
 * THE PANEL'S OWN COMMANDS — each one a control the panel already has, reached from the keyboard.
 *
 * None calls the model and none runs a tool, so none costs credit, asks for approval or writes an
 * audit row: they do exactly what clicking the matching control does. Commands that run tools are a
 * separate kind with their own governance, and do not belong in this file.
 *
 * Registered on import. The thread composer imports this module, so the commands exist wherever it
 * does; an app adds its own through `registerCommands`.
 */
import { t } from '../i18n';
import { pickOption, registerCommands } from './commands';
/** Resolve a typed argument against its options, and say so when nothing matches. */
function choose(a, options, arg, apply) {
    const o = pickOption(options, arg);
    if (!o) {
        a.note(t('ai.cmdNoMatch', { query: arg }));
        return;
    }
    apply(o);
}
export const PANEL_COMMANDS = [
    {
        name: 'new',
        descKey: 'ai.cmdDesc_new',
        available: (a) => a.can.newChat,
        run: (a) => void a.newChat(),
    },
    {
        name: 'stop',
        descKey: 'ai.cmdDesc_stop',
        available: (a) => a.can.thread,
        run: (a) => {
            if (!a.stop())
                a.note(t('ai.cmdNothingToStop'));
        },
    },
    {
        name: 'retry',
        descKey: 'ai.cmdDesc_retry',
        available: (a) => a.can.thread,
        run: (a) => {
            if (a.busy)
                a.note(t('ai.cmdBusy'));
            else if (!a.retry())
                a.note(t('ai.cmdNothingToRetry'));
        },
    },
    {
        name: 'fork',
        descKey: 'ai.cmdDesc_fork',
        available: (a) => a.can.fork,
        run: (a) => {
            if (!a.fork())
                a.note(t('ai.cmdNothingYet'));
        },
    },
    {
        name: 'rename',
        descKey: 'ai.cmdDesc_rename',
        available: (a) => a.can.rename,
        arg: { hintKey: 'ai.cmdArg_title', required: true },
        run: async (a, title) => {
            a.note((await a.rename(title)) ? t('ai.cmdRenamed', { title }) : t('ai.cmdRenameFailed'));
        },
    },
    {
        name: 'model',
        descKey: 'ai.cmdDesc_model',
        arg: { hintKey: 'ai.cmdArg_model', required: true, options: (a) => a.models },
        run: (a, arg) => choose(a, a.models, arg, (o) => {
            a.setModel(o.value);
            a.note(t('ai.cmdModelSet', { name: o.label }));
        }),
    },
    {
        name: 'effort',
        descKey: 'ai.cmdDesc_effort',
        arg: { hintKey: 'ai.cmdArg_level', required: true, options: (a) => a.efforts },
        run: (a, arg) => choose(a, a.efforts, arg, (o) => {
            a.setEffort(o.value);
            a.note(t('ai.cmdEffortSet', { level: o.label }));
        }),
    },
    {
        name: 'specialist',
        descKey: 'ai.cmdDesc_specialist',
        available: (a) => a.specialists.length > 0,
        arg: { hintKey: 'ai.cmdArg_specialist', required: true, options: (a) => a.specialists },
        run: (a, arg) => choose(a, a.specialists, arg, (o) => {
            a.setSpecialist(o.value);
            a.note(t('ai.cmdSpecialistSet', { name: o.label }));
        }),
    },
    {
        name: 'pin',
        descKey: 'ai.cmdDesc_pin',
        available: (a) => a.can.pin,
        arg: { hintKey: 'ai.cmdArg_title', required: false },
        run: (a, title) => a.note(a.pin(title) ? t('ai.cmdPinned') : t('ai.cmdNothingYet')),
    },
    {
        name: 'chapters',
        descKey: 'ai.cmdDesc_chapters',
        // A thread with no chapters has nothing to jump to, so the command is not offered there.
        available: (a) => a.chapters.length > 0,
        arg: { hintKey: 'ai.cmdArg_chapter', required: true, options: (a) => a.chapters },
        run: (a, arg) => choose(a, a.chapters, arg, (o) => a.jumpTo(Number(o.value))),
    },
    {
        name: 'delete',
        descKey: 'ai.cmdDesc_delete',
        available: (a) => a.can.delete,
        run: (a) => void a.confirmDelete(),
    },
    {
        name: 'help',
        descKey: 'ai.cmdDesc_help',
        run: (a) => a.showCommands(),
    },
];
registerCommands(...PANEL_COMMANDS);
//# sourceMappingURL=panel-commands.js.map